import { useMemo } from 'react';
import { App } from 'antd';

/** Caller-supplied options for a single toast. Mirrors `apps/portal`'s `ToastOptions` shape. */
export interface ToastOptions {
    /** Secondary line rendered under the message. */
    description?: string;
    /**
     * Auto-dismiss delay in milliseconds. Pass `null` to keep the toast
     * until dismissed manually — the built-in default for `error`, since a
     * failure must not vanish before it is read. `success`/`info` default
     * to 4500ms.
     */
    duration?: number | null;
}

/** The ergonomic surface call sites use. Mirrors `apps/portal`'s `ToastApi` shape. */
export interface ToastApi {
    success(message: string, options?: ToastOptions): string;
    error(message: string, options?: ToastOptions): string;
    info(message: string, options?: ToastOptions): string;
    /** Dismiss a toast early, e.g. once a longer-running action resolves. */
    dismiss(id: string): void;
}

type ToastVariant = 'success' | 'error' | 'info';

// EAA-1812: mirrors the portal kit's default (5000ms there vs 4500ms here —
// antd's own un-themed default is 4.5s, so this keeps parity with antd's
// baseline rather than inventing a third number for the same purpose).
const DEFAULT_DURATION_MS = 4500;

function defaultDurationMs(variant: ToastVariant): number | null {
    // EAA-1812: an error toast must stay on screen until the admin explicitly
    // dismisses it — same rule `apps/portal`'s `resolveDuration` encodes.
    return variant === 'error' ? null : DEFAULT_DURATION_MS;
}

let toastSeq = 0;

/**
 * Tier-1 presentational primitive: `useToast()` — the hook every screen calls
 * instead of importing antd's `notification` directly.
 *
 * antd ships two toast-shaped APIs: the static `notification` singleton
 * (importable directly from `'antd'`) and the context-aware one returned by
 * `App.useApp()`. The static singleton renders outside the nearest
 * `<ConfigProvider>`/`<App>` tree and therefore **silently ignores the app's
 * theme tokens** — it still works, it just always looks like default antd.
 * This hook reads `App.useApp()` so toasts pick up the mounted theme; it
 * requires the app to render antd's `<App>` component above anything that
 * calls `useToast()`. With no `<App>` ancestor, `App.useApp()` itself falls
 * back to the same un-themed static instance — there is no compile- or
 * render-time signal that this happened, so a missing `<App>` is a silent
 * theming regression, not a thrown error (see the divergence note below).
 *
 * API-compatible with `apps/portal/src/components/ui/useToast.ts` — same
 * method names (`success`/`error`/`info`/`dismiss`), same `ToastOptions`
 * shape (`description`, `duration` in **milliseconds**, `null` = no
 * auto-dismiss), same default-duration rule (errors persist, success/info
 * auto-dismiss). Divergences from the portal implementation, which could not
 * be closed without either giving `packages/ui` its own CSS or reimplementing
 * antd's `notification` from scratch (both against this ticket's brief):
 *   - `useToast()` here never throws for a missing provider — with no
 *     `<App>` ancestor it quietly degrades to unthemed toasts instead of the
 *     portal hook's loud `Error('useToast() called outside <ToastProvider>')`.
 *   - No cap on simultaneously visible toasts (portal caps at 4, dropping the
 *     oldest); antd's own stacking/limits apply instead.
 *   - No pause-on-hover/focus timer behavior; antd's `notification` does not
 *     expose that hook.
 */
export function useToast(): ToastApi {
    const { notification } = App.useApp();

    return useMemo<ToastApi>(() => {
        const open = (variant: ToastVariant, message: string, options?: ToastOptions): string => {
            toastSeq += 1;
            const key = `ui-toast-${toastSeq}`;
            const requested = options?.duration;
            const ms = requested === undefined ? defaultDurationMs(variant) : requested;

            notification[variant]({
                key,
                message,
                description: options?.description,
                // antd's `duration` is seconds, and `false` (not `0`) means
                // "never auto-close" — translate our ms/`null` contract here
                // so callers keep using the portal-compatible ms/`null` shape.
                duration: ms === null ? false : ms / 1000,
            });

            return key;
        };

        return {
            success: (message, options) => open('success', message, options),
            error: (message, options) => open('error', message, options),
            info: (message, options) => open('info', message, options),
            dismiss: (id: string) => notification.destroy(id),
        };
    }, [notification]);
}

export default useToast;
